import React from "react";
import {
	BsCheckCircle,
	BsDownload,
	BsExclamationTriangle,
	BsFileEarmarkExcel,
	BsXCircle,
} from "react-icons/bs";
import StepPanel from "../StepPanel";
import StatusBanner from "../StatusBanner";

const MAX_VISIBLE_FAILURES = 8;

const CompleteStep = ({ downloadInfo, onImportMore }) => {
	const summary = downloadInfo?.summary;
	const failedCount = summary?.failed || 0;
	const successCount = summary?.successful || 0;
	const totalCount = summary?.total || 0;
	const failures = summary?.failures || [];
	const hasFailures = failedCount > 0;
	const allFailed = hasFailures && successCount === 0 && totalCount > 0;
	const hiddenFailureCount = Math.max(0, failures.length - MAX_VISIBLE_FAILURES);

	const handleDownload = () => {
		if (!downloadInfo?.downloadUrl) return;
		const link = document.createElement("a");
		link.href = downloadInfo.downloadUrl;
		link.download = downloadInfo.filename || "books_import_result.xlsx";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const title = allFailed
		? "Import failed"
		: hasFailures
		? "Import finished with issues"
		: "Import complete";

	const description = allFailed
		? "None of the rows were imported. Review the reasons below, fix your spreadsheet, and try again."
		: hasFailures
		? "Some rows were imported. Failed rows are listed below with the reason each one was rejected."
		: "Your books have been processed. Download the result file if you want a copy of the row-level status.";

	return (
		<StepPanel
			stepNumber={3}
			title={title}
			description={description}
			status={
				<StatusBanner
					tone={hasFailures ? "warning" : "success"}
					title={
						totalCount
							? `${successCount} imported, ${failedCount} failed`
							: hasFailures
							? "Some rows failed"
							: "Import successful"
					}
				>
					{hasFailures
						? "Download the result spreadsheet for the full list of statuses and reasons."
						: "Attach covers and PDFs later with Bulk upload books if needed."}
				</StatusBanner>
			}
			nav={{
				hideBack: true,
				onNext: onImportMore,
				nextLabel: "Import more books",
				extra: (
					<button
						type="button"
						className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-akgreen bg-white px-5 text-sm font-semibold text-akgreenest transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2"
						onClick={handleDownload}
					>
						<BsDownload className="h-4 w-4" aria-hidden />
						Download result
					</button>
				),
			}}
		>
			<div className="space-y-4">
				<div
					className={`flex flex-col items-center rounded-2xl border px-6 py-8 text-center ${
						allFailed
							? "border-red-100 bg-red-50"
							: hasFailures
							? "border-amber-100 bg-amber-50"
							: "border-gray-100 bg-gray-50"
					}`}
				>
					<div
						className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
							allFailed
								? "bg-red-100 text-red-600"
								: hasFailures
								? "bg-amber-100 text-amber-700"
								: "bg-green-100 text-green-600"
						}`}
					>
						{allFailed ? (
							<BsXCircle className="h-8 w-8" aria-hidden />
						) : hasFailures ? (
							<BsExclamationTriangle className="h-8 w-8" aria-hidden />
						) : (
							<BsCheckCircle className="h-8 w-8" aria-hidden />
						)}
					</div>
					<p className="text-lg font-semibold text-gray-900">
						{allFailed
							? "No books were imported"
							: hasFailures
							? "Imported with some failed rows"
							: "Books imported successfully"}
					</p>
					<p className="mt-1 max-w-md text-sm text-gray-600">
						{totalCount
							? `${successCount} of ${totalCount} row${
									totalCount === 1 ? "" : "s"
							  } succeeded.`
							: "Attach covers and PDFs later with Bulk upload books if needed."}
					</p>
				</div>

				{hasFailures ? (
					<div className="overflow-hidden rounded-xl border border-red-200">
						<div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-4 py-3">
							<BsXCircle className="h-4 w-4 text-red-600" aria-hidden />
							<h3 className="text-sm font-semibold text-red-900">
								Failed rows ({failedCount})
							</h3>
						</div>
						<ul className="max-h-64 divide-y divide-red-50 overflow-y-auto bg-white">
							{failures.slice(0, MAX_VISIBLE_FAILURES).map((item) => (
								<li
									key={`${item.rowNumber}-${item.title}`}
									className="px-4 py-3"
								>
									<p className="text-sm font-medium text-gray-900">
										Row {item.rowNumber}
										{item.title ? `: ${item.title}` : ""}
									</p>
									<p className="mt-0.5 text-sm text-red-700">{item.reason}</p>
								</li>
							))}
						</ul>
						{hiddenFailureCount > 0 ? (
							<p className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-800">
								And {hiddenFailureCount} more in the result spreadsheet.
							</p>
						) : null}
					</div>
				) : (
					<div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
						<h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
							<BsFileEarmarkExcel className="text-akgreen" aria-hidden />
							Result file contains
						</h3>
						<ul className="space-y-2 text-sm text-gray-600">
							<li className="flex items-center gap-2">
								<span className="h-1.5 w-1.5 rounded-full bg-green-500" />
								Your original metadata columns
							</li>
							<li className="flex items-center gap-2">
								<span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
								Status (Success or Fail) for each row
							</li>
							<li className="flex items-center gap-2">
								<span className="h-1.5 w-1.5 rounded-full bg-red-500" />
								Reason for any failed row
							</li>
						</ul>
					</div>
				)}
			</div>
		</StepPanel>
	);
};

export default CompleteStep;
